import { forwardRef, useState, useEffect, useCallback, useId } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import {
  AppioWidgetEditor,
  type AppioWidgetEditorRef,
  type AppioWidgetEditorProps,
} from "appio-widget-editor";

interface EditorWrapperProps extends AppioWidgetEditorProps {
  active: boolean;
}

const PANEL_WIDTH = 300;

// Collapses the editor's built-in 300px side panels with scoped CSS, as the
// editor has no API for it.
export const EditorWrapper = forwardRef<AppioWidgetEditorRef, EditorWrapperProps>(
  function EditorWrapper({ active, ...editorProps }, ref) {
    const [leftOpen, setLeftOpen] = useState(true);
    const [rightOpen, setRightOpen] = useState(true);

    // Per instance, so the iOS/Android/watchOS editors don't collide.
    const rawId = useId();
    const scopeClass = "ew-" + rawId.replace(/:/g, "");

    const isSmallScreen = useCallback(() => {
      const width = window.innerWidth;
      const previewWidth = width - PANEL_WIDTH * 2;
      return previewWidth < width / 3;
    }, []);

    useEffect(() => {
      if (active && isSmallScreen()) {
        setLeftOpen(false);
        setRightOpen(false);
      }
    }, [active, isSmallScreen]);

    return (
      <div className="relative h-full w-full overflow-hidden">
        <button
          type="button"
          onClick={() => setLeftOpen((v) => !v)}
          className="absolute top-3 z-20 flex h-7 w-7 items-center justify-center rounded border border-gray-300 text-gray-500 hover:text-gray-800 cursor-pointer"
          style={{
            left: leftOpen ? PANEL_WIDTH + 8 : 8,
            backgroundColor: "#f0f0f0",
            transition: "left 200ms ease",
          }}
          aria-label={leftOpen ? "Collapse elements panel" : "Expand elements panel"}
        >
          {leftOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>

        <button
          type="button"
          onClick={() => setRightOpen((v) => !v)}
          className="absolute top-3 z-20 flex h-7 w-7 items-center justify-center rounded border border-gray-300 text-gray-500 hover:text-gray-800 cursor-pointer"
          style={{
            right: rightOpen ? PANEL_WIDTH + 8 : 8,
            backgroundColor: "#f0f0f0",
            transition: "right 200ms ease",
          }}
          aria-label={rightOpen ? "Collapse properties panel" : "Expand properties panel"}
        >
          {rightOpen ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>

        <style>{`
          .${scopeClass} > div > div:first-child {
            width: ${leftOpen ? PANEL_WIDTH : 0}px !important;
            opacity: ${leftOpen ? 1 : 0};
            overflow: hidden;
            transition: width 200ms ease, opacity 200ms ease;
          }
          .${scopeClass} > div > div:last-child {
            width: ${rightOpen ? PANEL_WIDTH : 0}px !important;
            opacity: ${rightOpen ? 1 : 0};
            overflow: hidden;
            transition: width 200ms ease, opacity 200ms ease;
          }
          /* Zoom controls move to the bottom so they don't overlap the toggle. */
          .${scopeClass} .relative.flex-1.min-w-0 > div:first-child {
            top: auto !important;
            bottom: 12px !important;
          }
        `}</style>
        <div className={`${scopeClass} h-full w-full`}>
          <AppioWidgetEditor ref={ref} {...editorProps} />
        </div>
      </div>
    );
  }
);
